import { DeStage } from "./types";

/**
 * Bố cục sơ đồ nhánh kép: ba khung có nhãn, và toạ độ tuyệt đối cho từng ô.
 *
 * VÌ SAO BA KHUNG: xếp tất cả thành một hàng cột thì "Vòng 3 — nhánh thua" đứng cạnh
 * "Vòng 2 — nhánh thắng" như thể chúng cùng một mạch, mà chúng là hai mạch khác hẳn -
 * một bên chưa thua trận nào, một bên thua thêm là về. Ba khung có nhãn là thứ duy
 * nhất nói ra được điều đó mà không cần ai giải thích.
 *
 * KHÔNG TÍNH LẠI LUẬT ĐI TIẾP. Hàm này chỉ đọc branch/stage/idx và các liên kết
 * winToId/loseToId mà server gửi xuống, để căn một ô vào giữa những ô nuôi nó. Ai đi
 * đâu là việc của lúc bốc thăm, và chỉ có một bản luật đó.
 *
 * CÓ MỘT BẢN TƯƠNG ĐƯƠNG Ở BACK OFFICE (App\Services\DoubleEliminationLayout::canvas).
 * Hai bản tồn tại vì đây là hai tầng trình bày khác nhau, chạy hai ngôn ngữ khác nhau,
 * và cỡ thẻ cũng khác: back office vẽ cho màn rộng, bản này vẽ cho điện thoại. Cái
 * PHẢI giống nhau là HÌNH DẠNG - ba khung, và ô vòng sau nằm giữa những ô nuôi nó -
 * chứ không phải con số. Sửa luật ở một bên thì sửa cả hai, và cả hai đều có test.
 */

/** Cỡ thẻ cho màn điện thoại. Hẹp hơn bản back office vì canvas phải vừa 390px. */
export const CARD_W = 150;

export const CARD_H = 62;

const COL_GAP = 44;

const ROW_GAP = 12;

const BAND_PAD = 12;

const BAND_HEAD = 46;

const BAND_GAP = 24;

export interface ONode {
  id: string;
  x: number;
  y: number;
}

export interface Khung {
  key: "win" | "lose" | "ko";
  label: string;
  sub: string;
  x: number;
  y: number;
  w: number;
  h: number;
  cols: { x: number; y: number; name: string }[];
  nodeIds: string[];
}

export interface BoCuc {
  width: number;
  height: number;
  bands: Khung[];
  nodes: Record<string, { x: number; y: number }>;
}

interface O {
  id: string;
  branch: string;
  stage: number;
  idx: number;
  winToId: string | null;
  loseToId: string | null;
}

const KHUNG: { key: Khung["key"]; branches: string[]; label: string; sub: string }[] = [
  { key: "win", branches: ["Q1", "Q2", "W"], label: "Nhánh thắng", sub: "Chưa thua trận nào" },
  { key: "lose", branches: ["Q3", "Q4", "L"], label: "Nhánh thua", sub: "Thua thêm là bị loại" },
  { key: "ko", branches: ["K", "GF", "GF2"], label: "Vòng chung kết", sub: "Thua là loại" },
];

export const tinhBoCuc = (stages: DeStage[]): BoCuc => {
  const o: O[] = [];
  const tenCot: Record<number, string> = {};

  for (const stage of stages) {
    tenCot[stage.stage] = stage.name;
    for (const node of stage.nodes) {
      o.push({
        id: node.nodeId || node.code,
        branch: stage.branch,
        stage: stage.stage,
        idx: node.idx,
        winToId: node.winToId ?? null,
        loseToId: node.loseToId ?? null,
      });
    }
  }

  const pos: Record<string, { x: number; y: number }> = {};
  const bands: Khung[] = [];
  let y = 0;
  let koX = 0;

  for (const k of KHUNG) {
    const cua = o.filter((n) => k.branches.includes(n.branch));
    if (cua.length === 0) {
      continue;
    }

    const stageList = Array.from(new Set(cua.map((n) => n.stage))).sort((a, b) => a - b);
    let x = BAND_PAD;
    const top = y + BAND_HEAD;
    let cao = 0;
    const cols: Khung["cols"] = [];

    for (const stage of stageList) {
      const trong = cua.filter((n) => n.stage === stage).sort((a, b) => a.idx - b.idx);
      let truoc: number | null = null;

      trong.forEach((node, i) => {
        /* Tâm những ô ĐÃ XẾP mà người thắng hoặc người thua của chúng vào ô này. */
        const nuoi = o
          .filter((n) => (n.winToId === node.id || n.loseToId === node.id) && pos[n.id])
          .map((n) => pos[n.id].y + CARD_H / 2);

        let yCua =
          nuoi.length === 0
            ? top + i * (CARD_H + ROW_GAP)
            : nuoi.reduce((a, b) => a + b, 0) / nuoi.length - CARD_H / 2;

        // Căn giữa có thể cho hai ô cùng một toạ độ; đẩy xuống theo thứ tự.
        if (truoc !== null) {
          yCua = Math.max(yCua, truoc + CARD_H + ROW_GAP);
        }
        yCua = Math.max(yCua, top);
        truoc = yCua;

        pos[node.id] = { x, y: Math.round(yCua) };
        cao = Math.max(cao, yCua + CARD_H - top);
      });

      cols.push({ x, y: top - 16, name: tenCot[stage] ?? `Vòng ${stage}` });
      x += CARD_W + COL_GAP;
    }

    const rong = x - COL_GAP + BAND_PAD;
    bands.push({
      key: k.key,
      label: k.label,
      sub: k.sub,
      x: 0,
      y,
      w: rong,
      h: cao + BAND_HEAD + BAND_PAD,
      cols,
      nodeIds: cua.map((n) => n.id),
    });

    y += cao + BAND_HEAD + BAND_PAD + BAND_GAP;
    koX = Math.max(koX, rong);
  }

  /* Khung vòng chung kết sang PHẢI hai khung kia và căn giữa theo chiều dọc: nó nhận
     người từ cả hai, nên nằm dưới cùng thì mọi đường nối đều phải vòng xuống và sơ đồ
     đọc ra như một mạch thứ ba nối tiếp chứ không phải một đích chung. */
  const i = bands.findIndex((b) => b.key === "ko");
  if (i >= 0 && bands.length > 1) {
    const khac = bands.filter((b) => b.key !== "ko");
    const dayKhac = Math.max(...khac.map((b) => b.y + b.h));
    const dx = koX + COL_GAP;
    const dy = Math.round((dayKhac - bands[i].h) / 2) - bands[i].y;

    bands[i].cols = bands[i].cols.map((c) => ({ ...c, x: c.x + dx, y: c.y + dy }));
    for (const id of bands[i].nodeIds) {
      if (pos[id]) {
        pos[id] = { x: pos[id].x + dx, y: pos[id].y + dy };
      }
    }
    bands[i].x += dx;
    bands[i].y += dy;
  }

  const width = bands.reduce((m, b) => Math.max(m, b.x + b.w), 0) + BAND_PAD;
  const height = bands.reduce((m, b) => Math.max(m, b.y + b.h), 0) + BAND_PAD;

  return { width, height, bands, nodes: pos };
};

/**
 * Nhãn cho ghế trống: "Thắng T3", "Thua L5".
 *
 * Dựng bằng cách ĐẢO NGƯỢC chính các liên kết server gửi xuống. Ở nhánh đơn nhìn vị
 * trí là biết ai sẽ vào; ở nhánh kép thì không, vì người thua cũng đi tiếp sang một ô
 * khác - in "—" là bắt người xem tự đoán.
 */
export const nhanGheTrong = (stages: DeStage[]): Record<string, Record<number, string>> => {
  const out: Record<string, Record<number, string>> = {};
  const ghi = (id: string | null | undefined, slot: number | null | undefined, nhan: string) => {
    if (!id || !slot) {
      return;
    }
    out[id] = out[id] || {};
    out[id][slot] = nhan;
  };

  for (const stage of stages) {
    for (const node of stage.nodes) {
      ghi(node.winToId, node.winToSlot, `Thắng ${node.code}`);
      ghi(node.loseToId, node.loseToSlot, `Thua ${node.code}`);
    }
  }

  return out;
};
